import {
  Autocomplete,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PerformanceTable from "./PerformanceTable";
import { Box, Grid } from "@mui/system";
import SquareIcon from "@mui/icons-material/Square";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  selectBizDashboardLoading,
  selectBizOutputMetadata,
  selectBizQueryOutput,
  selectBizComparisonQueryOutput,
  selectBizComparisonLoading,
  selectBizIsPerformanceApiCalled,
  setIsPerformanceApiCalled,
} from "../../../../../features/bizDevelopmentDashboardSlice";
import { useBizDevelopmentDashboard } from "../../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboard";
import {
  buildColumns,
  exportToCSV,
} from "../../helpers/bizDevelopmentDashboardHelpers";
import FloatingActionItems from "../../../../../pages/FloatingItems/FloatingActionItems";

interface TimeframeOption {
  value: string;
  label: string;
}

const Performance = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const queryOutput = useSelector(selectBizQueryOutput);
  const outputMetadata = useSelector(selectBizOutputMetadata);
  const isLoading = useSelector(selectBizDashboardLoading);
  const comparisonQueryOutput = useSelector(selectBizComparisonQueryOutput);
  const isComparisonLoading = useSelector(selectBizComparisonLoading);
  const isPerformanceApiCalled = useSelector(selectBizIsPerformanceApiCalled);
  const {
    fetchDashboardData,
    fetchComparisonData,
    clearComparisonData,
    getSitesFromParams,
  } = useBizDevelopmentDashboard();

  const fmt = (d: dayjs.Dayjs) => d.format("DD/MM/YYYY");

  const timeframeOptions = useMemo(
    (): TimeframeOption[] => [
      {
        value: "yesterday",
        label: `Yesterday (${fmt(dayjs().subtract(1, "day"))})`,
      },
      {
        value: "two_days_ago",
        label: `2 Days Ago (${fmt(dayjs().subtract(2, "day"))})`,
      },
      {
        value: "three_days_ago",
        label: `3 Days Ago (${fmt(dayjs().subtract(3, "day"))})`,
      },
      { value: "mtd", label: "MTD" },
    ],
    [],
  );

  const [selectedTimeframe, setSelectedTimeframe] = useState<TimeframeOption>(
    timeframeOptions.find(
      (o) =>
        o.value === (searchParams.get("timeframePerformance") ?? "yesterday"),
    ) ?? timeframeOptions[0],
  );

  const [selectedAdditional, setSelectedAdditional] =
    useState<TimeframeOption | null>(() => {
      const saved = searchParams.get("additionalTimeframePerformance");
      return saved
        ? (timeframeOptions.find((o) => o.value === saved) ?? null)
        : null;
    });

  const additionalTimeframeOptions = useMemo((): TimeframeOption[] => {
    const all: TimeframeOption[] = [
      {
        value: "yesterday",
        label: `Yesterday (${fmt(dayjs().subtract(1, "day"))})`,
      },
      {
        value: "two_days_ago",
        label: `2 Days Ago (${fmt(dayjs().subtract(2, "day"))})`,
      },
      {
        value: "three_days_ago",
        label: `3 Days Ago (${fmt(dayjs().subtract(3, "day"))})`,
      },
    ];
    return all.filter((o) => o.value !== selectedTimeframe.value);
  }, [selectedTimeframe]);

  useEffect(() => {
    if (isPerformanceApiCalled) return;
    const sites = getSitesFromParams();
    if (sites.length === 0) return;
    fetchDashboardData(selectedTimeframe.value, sites);
    dispatch(setIsPerformanceApiCalled(true));
    if (selectedAdditional) {
      fetchComparisonData(selectedAdditional.value, sites);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPerformanceApiCalled]);

  const handleTimeframeChange = (_: any, newValue: TimeframeOption | null) => {
    if (!newValue) return;
    setSelectedTimeframe(newValue);
    setSelectedAdditional(null);
    clearComparisonData();
    setSearchParams((prev) => {
      prev.set("timeframePerformance", newValue.value);
      prev.delete("additionalTimeframePerformance");
      return prev;
    });
    fetchDashboardData(newValue.value, getSitesFromParams());
    dispatch(setIsPerformanceApiCalled(true));
  };

  const handleAdditionalChange = (_: any, newValue: TimeframeOption | null) => {
    setSelectedAdditional(newValue);
    setSearchParams((prev) => {
      if (newValue) {
        prev.set("additionalTimeframePerformance", newValue.value);
      } else {
        prev.delete("additionalTimeframePerformance");
      }
      return prev;
    });
    if (newValue) {
      fetchComparisonData(newValue.value, getSitesFromParams());
    } else {
      clearComparisonData();
    }
  };

  const isMtd = searchParams.get("timeframePerformance") === "mtd";

  const handleDownload = () => {
    const columns = buildColumns(outputMetadata, isMtd);

    exportToCSV({
      columns,
      primaryRows: queryOutput,
      comparisonRows: selectedAdditional ? (comparisonQueryOutput ?? []) : [],
      primaryLabel: selectedTimeframe.label,
      comparisonLabel: selectedAdditional?.label ?? "",
      isMtd,
      filename: `performance_${selectedTimeframe.value}${selectedAdditional ? `_vs_${selectedAdditional.value}` : ""}`,
    });
  };

  return (
    <>
      <Grid container spacing={2} alignItems="center" sx={{ p: 2 }}>
        <Grid size={3}>
          <Autocomplete
            disablePortal
            options={timeframeOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            value={selectedTimeframe}
            onChange={handleTimeframeChange}
            fullWidth
            size="small"
            sx={{ pt: "8px" }}
            renderInput={(params) => (
              <TextField
                color="secondary"
                {...params}
                required
                placeholder="Search Time Frame or choose from the list"
                label="Time Frame"
                data-tracker="799"
              />
            )}
          />
        </Grid>
        <Grid size={3}>
          <Autocomplete
            disablePortal
            disabled={selectedTimeframe.value === "mtd"}
            options={additionalTimeframeOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            value={selectedAdditional}
            onChange={handleAdditionalChange}
            fullWidth
            size="small"
            sx={{ pt: "8px" }}
            renderInput={(params) => (
              <TextField
                data-tracker="800"
                color="secondary"
                {...params}
                placeholder="Compare"
                label="Additional Time Frame"
                slotProps={{
                  input: {
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <AddIcon sx={{ marginLeft: "12px" }} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
        </Grid>
        <Grid size={6}>
          <Box display={"flex"} justifyContent={"flex-end"}>
            <IconButton
              aria-label="refresh"
              color="secondary"
              onClick={() => {
                const sites = getSitesFromParams();
                fetchDashboardData(selectedTimeframe.value, sites);
                dispatch(setIsPerformanceApiCalled(true));
                if (selectedAdditional) {
                  fetchComparisonData(selectedAdditional.value, sites);
                }
              }}
              data-tracker="802"
            >
              <RefreshIcon />
            </IconButton>
            <IconButton
              aria-label="download"
              color="secondary"
              sx={{ ml: 2 }}
              onClick={handleDownload}
              data-tracker="801"
            >
              <DownloadIcon />
            </IconButton>
          </Box>
        </Grid>
        {selectedAdditional && (
          <Grid size={12}>
            <Box display={"flex"} alignItems={"center"}>
              <Typography
                display={"flex"}
                alignItems={"center"}
                fontSize={"12px"}
                mr={2}
              >
                <SquareIcon
                  sx={{
                    color: "#000000DE",
                    mr: 0.2,
                    fontSize: "20px",
                    borderRadius: "16px",
                  }}
                />
                {selectedTimeframe.label}
              </Typography>
              {selectedAdditional && (
                <Typography
                  display={"flex"}
                  alignItems={"center"}
                  fontSize={"12px"}
                >
                  <SquareIcon
                    sx={{
                      color: "#00000061",
                      mr: 0.2,
                      fontSize: "20px",
                      borderRadius: "16px",
                    }}
                  />
                  {selectedAdditional.label}
                </Typography>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
      <PerformanceTable
        outputMetadata={outputMetadata}
        queryOutput={queryOutput}
        isLoading={isLoading}
        comparisonQueryOutput={selectedAdditional ? comparisonQueryOutput : []}
        isComparisonLoading={isComparisonLoading}
        isComparisonActive={selectedAdditional !== null}
      />
      <div data-tracker="803">
        <FloatingActionItems
          selectedSiteFloatingAction={getSitesFromParams().join(",")}
          selectedDimensionsFloatingAction=""
          trackIdFloatingAction="804"
          disabledButton={queryOutput.length === 0}
        />
      </div>
    </>
  );
};

export default Performance;
